/**
 * cacheManager.js — TrendMing production cache management utility
 *
 * Clears every layer of client-side caching:
 *   • localStorage (app cache keys + optional auth preserve)
 *   • sessionStorage
 *   • Cache Storage API (all SW buckets)
 *   • IndexedDB databases
 *   • Service Worker unregistration
 *   • Internal app news cache (cache.js / tm_cache_*)
 *   • App-version stored reference
 *
 * Safe:  auth tokens are PRESERVED by default so the user stays logged in.
 * Async: all operations are awaited and errors are isolated — one failing
 *        step never aborts the rest.
 */

import { cacheClear } from "@/lib/cache";

// ─── Storage key constants (must stay in sync with AuthContext + cache.js) ───
const TOKEN_KEY        = "tm_token";
const USER_KEY         = "tm_user";
const CREATOR_MODE_KEY = "tm_creator_mode";
const APP_VERSION_KEY  = "tm_app_version";
const CACHE_PREFIX     = "tm_cache_";

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * @typedef {Object} ClearResult
 * @property {boolean} localStorage
 * @property {boolean} sessionStorage
 * @property {boolean} cacheStorage
 * @property {boolean} indexedDB
 * @property {boolean} serviceWorkers
 * @property {boolean} appCache
 */

/**
 * @typedef {Object} ClearReport
 * @property {ClearResult} results      - per-layer success flags
 * @property {Array}       errors       - { layer, message } for each failure
 * @property {boolean}     success      - true when zero errors
 * @property {number}      clearedLayers
 * @property {number}      timestamp
 */

// ─── Internal helpers ─────────────────────────────────────────────────────────

/** Send a typed message to the active service worker (if any). */
async function _messageServiceWorker(msg) {
  if (!("serviceWorker" in navigator)) return;
  const reg = await navigator.serviceWorker.getRegistration().catch(() => null);
  if (reg?.active) {
    reg.active.postMessage(msg);
  }
}

/** Wrap a risky operation so it never throws — returns { ok, error }. */
async function _attempt(layer, fn) {
  try {
    await fn();
    return { layer, ok: true };
  } catch (e) {
    console.warn(`[cacheManager] ${layer} failed:`, e);
    return { layer, ok: false, message: e?.message ?? String(e) };
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Clear ALL client-side caches.
 *
 * @param {Object}  [opts]
 * @param {boolean} [opts.preserveAuth=true]   Keep JWT token + user object in localStorage.
 * @param {boolean} [opts.unregisterSW=true]   Unregister every SW registration.
 * @param {boolean} [opts.clearIndexedDB=true] Drop all IndexedDB databases.
 * @returns {Promise<ClearReport>}
 */
export async function clearAllCaches({
  preserveAuth   = true,
  unregisterSW   = true,
  clearIndexedDB = true,
} = {}) {
  const attempts = [];

  // ── 1. Snapshot auth tokens BEFORE clearing localStorage ─────────────────
  const savedAuth = preserveAuth
    ? {
        [TOKEN_KEY]:        localStorage.getItem(TOKEN_KEY),
        [USER_KEY]:         localStorage.getItem(USER_KEY),
        [CREATOR_MODE_KEY]: localStorage.getItem(CREATOR_MODE_KEY),
      }
    : null;

  // ── 2. localStorage ───────────────────────────────────────────────────────
  attempts.push(await _attempt("localStorage", () => {
    localStorage.clear();
    // Restore auth immediately so the user stays logged in after the flush
    if (savedAuth) {
      Object.entries(savedAuth).forEach(([k, v]) => {
        if (v !== null) localStorage.setItem(k, v);
      });
    }
  }));

  // ── 3. sessionStorage ─────────────────────────────────────────────────────
  attempts.push(await _attempt("sessionStorage", () => {
    sessionStorage.clear();
  }));

  // ── 4. Cache Storage API (service worker caches) ──────────────────────────
  attempts.push(await _attempt("cacheStorage", async () => {
    if (!("caches" in window)) return;
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  }));

  // ── 5. IndexedDB ──────────────────────────────────────────────────────────
  if (clearIndexedDB) {
    attempts.push(await _attempt("indexedDB", async () => {
      if (!window.indexedDB?.databases) return; // Safari < 14 lacks .databases()
      const dbs = await window.indexedDB.databases();
      await Promise.all(
        dbs.map(
          ({ name }) =>
            new Promise((res, rej) => {
              const req = window.indexedDB.deleteDatabase(name);
              req.onsuccess = res;
              req.onerror   = () => rej(req.error);
              req.onblocked = res; // unblock rather than hang forever
            })
        )
      );
    }));
  }

  // ── 6. Service worker unregistration ─────────────────────────────────────
  if (unregisterSW) {
    attempts.push(await _attempt("serviceWorkers", async () => {
      if (!("serviceWorker" in navigator)) return;
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }));
  }

  // ── 7. Internal app news cache (tm_cache_* keys via cache.js) ─────────────
  attempts.push(await _attempt("appCache", () => {
    cacheClear(); // removes all localStorage keys with tm_cache_ prefix
  }));

  // ── 8. Stored app-version reference (force fresh fetch on next load) ───────
  attempts.push(await _attempt("appVersion", () => {
    localStorage.removeItem(APP_VERSION_KEY);
  }));

  // ── 9. Tell the SW to wipe its own in-memory state ────────────────────────
  await _messageServiceWorker({ type: "CLEAR_CACHE" }).catch(() => {});

  // ── Build report ──────────────────────────────────────────────────────────
  const results = Object.fromEntries(attempts.map(({ layer, ok }) => [layer, ok]));
  const errors  = attempts
    .filter(({ ok }) => !ok)
    .map(({ layer, message }) => ({ layer, message }));

  const report = {
    results,
    errors,
    success:       errors.length === 0,
    clearedLayers: attempts.filter(({ ok }) => ok).length,
    timestamp:     Date.now(),
  };

  console.info("[cacheManager] Clear complete:", report);
  return report;
}

/**
 * Lightweight "soft" clear — only wipes the app's own localStorage news cache
 * and Cache Storage buckets.  Leaves auth + IndexedDB + SW registrations intact.
 * Useful for "refresh data" scenarios, not full cache busting.
 *
 * @returns {Promise<ClearReport>}
 */
export async function clearAppCacheOnly() {
  return clearAllCaches({
    preserveAuth:   true,
    unregisterSW:   false,
    clearIndexedDB: false,
  });
}

// ─── Version management ───────────────────────────────────────────────────────

/**
 * Fetch the server's current version manifest (/version.json).
 * Always bypasses the browser cache via cache-busting query param.
 *
 * @returns {Promise<{version: string, buildTime: string, buildId: string}|null>}
 */
export async function fetchAppVersion() {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: "no-store",
      headers: { "Cache-Control": "no-cache" },
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

/** Return the version string stored from the last successful page load. */
export function getStoredAppVersion() {
  return localStorage.getItem(APP_VERSION_KEY) ?? null;
}

/** Persist the current server version to localStorage for future comparison. */
export function storeAppVersion(version) {
  if (version) localStorage.setItem(APP_VERSION_KEY, version);
}

/**
 * Compare the server's current version against the locally stored one.
 * Returns `true` when a new deploy is available.
 *
 * @returns {Promise<boolean>}
 */
export async function isVersionOutdated() {
  const remote = await fetchAppVersion();
  if (!remote?.version) return false;          // can't determine — assume fine
  const stored = getStoredAppVersion();
  if (!stored) {
    storeAppVersion(remote.version);           // first visit — just record it
    return false;
  }
  return remote.version !== stored;
}

// ─── Cache diagnostics ────────────────────────────────────────────────────────

/**
 * Return a snapshot of what's currently sitting in the various caches.
 * Non-destructive — read-only.
 *
 * @returns {Promise<{
 *   localStorageKeys: number,
 *   appCacheKeys: number,
 *   cacheStorageBuckets: number,
 *   cacheStorageEntries: number,
 *   serviceWorkers: number,
 *   storedVersion: string|null,
 * }>}
 */
export async function getCacheStats() {
  const stats = {
    localStorageKeys:      0,
    appCacheKeys:          0,
    cacheStorageBuckets:   0,
    cacheStorageEntries:   0,
    serviceWorkers:        0,
    storedVersion:         getStoredAppVersion(),
  };

  try { stats.localStorageKeys = localStorage.length; } catch {}
  try {
    stats.appCacheKeys = Object.keys(localStorage)
      .filter((k) => k.startsWith(CACHE_PREFIX)).length;
  } catch {}

  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      stats.cacheStorageBuckets = keys.length;
      const counts = await Promise.all(
        keys.map(async (k) => {
          const c = await caches.open(k);
          return (await c.keys()).length;
        })
      );
      stats.cacheStorageEntries = counts.reduce((a, b) => a + b, 0);
    }
  } catch {}

  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      stats.serviceWorkers = regs.length;
    }
  } catch {}

  return stats;
}

/**
 * Force a hard reload after a short delay, bypassing the browser cache.
 * Pass `delay` (ms) to allow toast animations to finish first.
 */
export function forceReload(delay = 1500) {
  setTimeout(() => {
    // location.reload(true) is deprecated in modern browsers but still works;
    // the cache-busting query param is the reliable fallback.
    try {
      window.location.reload();
    } catch {
      window.location.href = window.location.href.split("?")[0] + "?_cb=" + Date.now();
    }
  }, delay);
}
